import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Anchor, Badge, Center, Loader, Stack, Table, Text } from '@mantine/core';
import { IconWorldWww } from '@tabler/icons-react';
import { api } from '../../lib/api';
import { DNS_ZONES_QUERY_KEY } from '../../lib/api/domains/dnsZones';
import { DnsStatusBadge, formatDnsTime } from '../../components/dnsZones/DnsZoneParts';
import { InfoEmpty } from './CustomerInfoParts';

/**
 * Kunden-Flyout, Reiter „DNS-Zonen“ (v1.408.0): bestätigt zugeordnete Zonen dieses Kunden plus offene
 * Vorschläge für ihn. Details, Versionen und Export auf der Zonen-Seite.
 */
export function CustomerDnsZonesTab({ customerId, active }: { customerId: string; active: boolean }) {
  const { data, isLoading } = useQuery({
    queryKey: [...DNS_ZONES_QUERY_KEY, 'customer', customerId],
    queryFn: () => api.listDnsZones({ customerId, missing: true }),
    enabled: active,
  });
  const suggested = useQuery({
    queryKey: [...DNS_ZONES_QUERY_KEY, 'customer-suggested', customerId],
    queryFn: async () => (await api.listDnsZones({ status: ['SUGGESTED'] })).zones.filter((z) => z.assignment.suggestedCustomer?.id === customerId),
    enabled: active,
  });

  if (isLoading) return <Center py="xl"><Loader size="sm" /></Center>;
  const zones = data?.zones ?? [];
  if (zones.length === 0 && !suggested.data?.length) {
    return <InfoEmpty icon={<IconWorldWww size={20} />}>Keine DNS-Zonen zugeordnet</InfoEmpty>;
  }
  return (
    <Stack gap="sm">
      {zones.length > 0 && (
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Zone</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Konto</Table.Th>
              <Table.Th>Zuletzt geändert</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {zones.map((z) => (
              <Table.Tr key={z.id}>
                <Table.Td>
                  <Anchor component={Link} to={`/dns-zonen/${z.id}`}>{z.name}</Anchor>
                  {!z.presentInAccount && <Badge ml={6} size="xs" color="gray">nicht mehr im Konto</Badge>}
                </Table.Td>
                <Table.Td><DnsStatusBadge status={z.assignment.status} /></Table.Td>
                <Table.Td>{z.accountLabel}</Table.Td>
                <Table.Td>{formatDnsTime(z.lastChangedAt)}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
      {!!suggested.data?.length && (
        <Text size="sm" c="dimmed">
          Offene Vorschläge für diesen Kunden: {suggested.data.map((z) => z.name).join(', ')} — Freigabe unter{' '}
          <Anchor component={Link} to="/dns-zonen">DNS-Zonen</Anchor>.
        </Text>
      )}
    </Stack>
  );
}
